/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/**
 * RGBARRY-CUSTOMIZATION: Klaviyo footer subscribe hook. Non-navigating mutation via
 * useFetcher, posting JSON to the klaviyo-subscribe server-action proxy.
 */
import { useFetcher } from 'react-router';
import { useFetcherEffect } from '@/hooks/use-fetcher-effect';
import { createLogger } from '@/lib/logger';
import { resourceRoutes } from '@/route-paths';
import type { action as klaviyoSubscribeAction, KlaviyoSubscribeResponse } from '@/routes/action.klaviyo-subscribe';

const logger = createLogger();

/** Payload for a single subscribe: `email`/`checkout` need `email`; `sms` needs `phone` (US format). */
export interface KlaviyoSubscribePayload {
    source: 'email' | 'checkout' | 'sms';
    email?: string;
    phone?: string;
}

/**
 * Hook for subscribing a shopper to a Klaviyo marketing list (email or SMS).
 *
 * @param onSuccess - Called after Klaviyo accepts the subscription.
 * @param onError - Called when the action returns success: false; receives the message.
 * @returns subscribe(payload), isSubmitting (true only while fetcher.state === 'submitting')
 */
export function useKlaviyoSubscribe(
    onSuccess?: () => void,
    onError?: (message: string, data?: KlaviyoSubscribeResponse) => void
): {
    subscribe: (payload: KlaviyoSubscribePayload) => void;
    isSubmitting: boolean;
} {
    const fetcher = useFetcher<typeof klaviyoSubscribeAction>();

    useFetcherEffect(fetcher, {
        onSuccess: () => {
            onSuccess?.();
        },
        onError: (error, data) => {
            const message = Array.isArray(error) ? error.join(', ') : error;
            logger.error('Klaviyo subscribe failed', { message });
            onError?.(message, data);
        },
    });

    const subscribe = (payload: KlaviyoSubscribePayload): void => {
        void fetcher.submit(payload as unknown as FormData, {
            method: 'POST',
            action: resourceRoutes.klaviyoSubscribe,
            encType: 'application/json',
        });
    };

    const isSubmitting = fetcher.state === 'submitting';

    return { subscribe, isSubmitting };
}
